export const PrivateConfidentialSale = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-4xl font-bold mb-6 text-gray-900">
              Keep Your Sale Private to Avoid Tenant Panic
            </h2>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              One of the biggest challenges when selling an apartment building is managing tenant anxiety. When tenants learn their building is for sale, they often panic about potential rent increases, new management policies, or even eviction—even when none of these concerns are warranted.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              A direct, private sale allows you to maintain confidentiality throughout the transaction. Unlike traditional listings that appear on the MLS, commercial real estate websites, and yard signs, a for sale by owner transaction with a direct buyer can remain completely confidential until closing.
            </p>
            
            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg mb-6">
              <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                <i className="ri-alert-line text-amber-600"></i>
                The Tenant Panic Problem
              </h3>
              <p className="text-gray-700 mb-3">
                When tenants discover their building is for sale through public listings, several problems often occur:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-amber-600 flex-shrink-0 mt-1"></i>
                  <span>Tenants start looking for new apartments "just in case"</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-amber-600 flex-shrink-0 mt-1"></i>
                  <span>Good tenants move out, leaving you with vacancies</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-amber-600 flex-shrink-0 mt-1"></i>
                  <span>Remaining tenants become difficult or stop paying rent</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-amber-600 flex-shrink-0 mt-1"></i>
                  <span>Property value decreases due to increased vacancy</span>
                </li>
              </ul>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-eye-off-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">No Public Listing</h4>
                  <p className="text-gray-700">Your property never appears on MLS or commercial real estate websites</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-forbid-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">No For Sale Signs</h4>
                  <p className="text-gray-700">No yard signs or banners alerting tenants and neighbors to the sale</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-lock-password-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Confidential Negotiations</h4>
                  <p className="text-gray-700">All discussions and paperwork handled discreetly between you and the buyer</p>
                </div>
              </div>
            </div>
          </div>
          <div className="relative">
            <img
              src="https://public.readdy.ai/ai/img_res/931fda4963eccea7664a7f89fba8281f.jpg"
              alt="Private confidential apartment building sale transaction"
              className="w-full h-full rounded-lg shadow-xl object-cover"
            />
          </div>
        </div>

        <div className="mt-16 bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-8 md:p-12">
          <h3 className="text-2xl font-bold mb-8 text-gray-900 text-center">
            How We Maintain Complete Confidentiality
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="text-4xl font-bold text-teal-600 mb-2">1</div>
              <h4 className="font-bold text-gray-900 mb-3">Initial Contact</h4>
              <p className="text-gray-700">
                You reach out to us directly. No listing, no agents, no public announcement. Just a private conversation about your property.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="text-4xl font-bold text-teal-600 mb-2">2</div>
              <h4 className="font-bold text-gray-900 mb-3">Discrete Evaluation</h4>
              <p className="text-gray-700">
                We review your property information privately and schedule a single, low-key walkthrough that doesn't raise tenant suspicions.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="text-4xl font-bold text-teal-600 mb-2">3</div>
              <h4 className="font-bold text-gray-900 mb-3">Private Closing</h4>
              <p className="text-gray-700">
                We close the transaction confidentially. Tenants only learn about the ownership change when legally required after closing.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};